/**
 * Formula Engine for Custom Report Builder
 * Parses and evaluates user-defined formulas with field references and functions
 */
import { getFieldByKey } from './FieldRegistry.js';
import { resolveColumnValue, ensureCalcResult } from './ColumnResolvers.js';

/**
 * Token types for the formula lexer
 */
const TokenType = {
	NUMBER: 'NUMBER',
	STRING: 'STRING',
	IDENTIFIER: 'IDENTIFIER',
	OPERATOR: 'OPERATOR',
	COMPARISON: 'COMPARISON',
	LPAREN: 'LPAREN',
	RPAREN: 'RPAREN',
	COMMA: 'COMMA',
	EOF: 'EOF',
};

/**
 * Tokenize a formula string into tokens
 * @param {string} formula - Formula string to tokenize
 * @returns {Array} Array of tokens
 */
function tokenize(formula) {
	const tokens = [];
	let pos = 0;

	while (pos < formula.length) {
		let char = formula[pos];

		// Skip whitespace
		if (/\s/.test(char)) {
			pos++;
			continue;
		}

		// Numbers (including decimals)
		if (/\d/.test(char) || (char === '.' && /\d/.test(formula[pos + 1]))) {
			let num = '';
			while (pos < formula.length && (/\d/.test(formula[pos]) || formula[pos] === '.')) {
				num += formula[pos++];
			}
			tokens.push({ type: TokenType.NUMBER, value: parseFloat(num) });
			continue;
		}

		// Strings (single or double quoted)
		if (char === '"' || char === "'") {
			const quote = char;
			let str = '';
			pos++; // Skip opening quote
			while (pos < formula.length && formula[pos] !== quote) {
				if (formula[pos] === '\\' && pos + 1 < formula.length) {
					pos++; // Skip escape character
				}
				str += formula[pos++];
			}
			pos++; // Skip closing quote
			tokens.push({ type: TokenType.STRING, value: str });
			continue;
		}

		// Identifiers with dot notation support (e.g., receiver.tags, user.clients.asplundh)
		if (/[a-zA-Z_]/.test(char)) {
			let id = '';
			while (pos < formula.length) {
				if (/[a-zA-Z0-9_]/.test(formula[pos])) {
					id += formula[pos++];
				} else if (formula[pos] === '.' && pos + 1 < formula.length && /[a-zA-Z_]/.test(formula[pos + 1])) {
					// Allow dot followed by letter (property access like receiver.tags)
					id += formula[pos++];
				} else {
					break;
				}
			}
			tokens.push({ type: TokenType.IDENTIFIER, value: id });
			continue;
		}

		// Comparison operators (==, !=, <=, >=, <, >)
		if (char === '=' && formula[pos + 1] === '=') {
			tokens.push({ type: TokenType.COMPARISON, value: '==' });
			pos += 2;
			continue;
		}
		if (char === '!' && formula[pos + 1] === '=') {
			tokens.push({ type: TokenType.COMPARISON, value: '!=' });
			pos += 2;
			continue;
		}
		if (char === '<' && formula[pos + 1] === '=') {
			tokens.push({ type: TokenType.COMPARISON, value: '<=' });
			pos += 2;
			continue;
		}
		if (char === '>' && formula[pos + 1] === '=') {
			tokens.push({ type: TokenType.COMPARISON, value: '>=' });
			pos += 2;
			continue;
		}
		if (char === '<') {
			tokens.push({ type: TokenType.COMPARISON, value: '<' });
			pos++;
			continue;
		}
		if (char === '>') {
			tokens.push({ type: TokenType.COMPARISON, value: '>' });
			pos++;
			continue;
		}

		// Arithmetic operators
		if (['+', '-', '*', '/'].includes(char)) {
			tokens.push({ type: TokenType.OPERATOR, value: char });
			pos++;
			continue;
		}

		// Parentheses
		if (char === '(') {
			tokens.push({ type: TokenType.LPAREN, value: '(' });
			pos++;
			continue;
		}
		if (char === ')') {
			tokens.push({ type: TokenType.RPAREN, value: ')' });
			pos++;
			continue;
		}

		// Comma
		if (char === ',') {
			tokens.push({ type: TokenType.COMMA, value: ',' });
			pos++;
			continue;
		}

		// Unknown character - skip it
		pos++;
	}

	tokens.push({ type: TokenType.EOF, value: null });
	return tokens;
}

/**
 * Parse tokens into an AST
 */
class Parser {
	constructor(tokens) {
		this.tokens = tokens;
		this.pos = 0;
	}

	current() {
		return this.tokens[this.pos];
	}

	consume(expectedType) {
		const token = this.current();
		if (token.type !== expectedType) {
			throw new Error(`Expected ${expectedType} but got ${token.type}`);
		}
		this.pos++;
		return token;
	}

	parse() {
		return this.parseExpression();
	}

	parseExpression() {
		return this.parseComparison();
	}

	parseComparison() {
		let left = this.parseAdditive();

		while (this.current().type === TokenType.COMPARISON) {
			const op = this.consume(TokenType.COMPARISON).value;
			const right = this.parseAdditive();
			left = { type: 'comparison', operator: op, left, right };
		}

		return left;
	}

	parseAdditive() {
		let left = this.parseMultiplicative();

		while (this.current().type === TokenType.OPERATOR && ['+', '-'].includes(this.current().value)) {
			const op = this.consume(TokenType.OPERATOR).value;
			const right = this.parseMultiplicative();
			left = { type: 'binary', operator: op, left, right };
		}

		return left;
	}

	parseMultiplicative() {
		let left = this.parseUnary();

		while (this.current().type === TokenType.OPERATOR && ['*', '/'].includes(this.current().value)) {
			const op = this.consume(TokenType.OPERATOR).value;
			const right = this.parseUnary();
			left = { type: 'binary', operator: op, left, right };
		}

		return left;
	}

	parseUnary() {
		if (this.current().type === TokenType.OPERATOR && this.current().value === '-') {
			this.consume(TokenType.OPERATOR);
			const operand = this.parseUnary();
			return { type: 'unary', operator: '-', operand };
		}
		return this.parsePrimary();
	}

	parsePrimary() {
		const token = this.current();

		// Number literal
		if (token.type === TokenType.NUMBER) {
			this.consume(TokenType.NUMBER);
			return { type: 'number', value: token.value };
		}

		// String literal
		if (token.type === TokenType.STRING) {
			this.consume(TokenType.STRING);
			return { type: 'string', value: token.value };
		}

		// Identifier (field reference or function call)
		if (token.type === TokenType.IDENTIFIER) {
			const name = this.consume(TokenType.IDENTIFIER).value;

			// Check if it's a function call
			if (this.current().type === TokenType.LPAREN) {
				return this.parseFunctionCall(name);
			}

			// It's a field reference
			return { type: 'field', name };
		}

		// Parenthesized expression
		if (token.type === TokenType.LPAREN) {
			this.consume(TokenType.LPAREN);
			const expr = this.parseExpression();
			this.consume(TokenType.RPAREN);
			return expr;
		}

		throw new Error(`Unexpected token: ${token.type}`);
	}

	parseFunctionCall(name) {
		this.consume(TokenType.LPAREN);
		const args = [];

		if (this.current().type !== TokenType.RPAREN) {
			args.push(this.parseExpression());

			while (this.current().type === TokenType.COMMA) {
				this.consume(TokenType.COMMA);
				args.push(this.parseExpression());
			}
		}

		this.consume(TokenType.RPAREN);
		return { type: 'function', name: name.toUpperCase(), args };
	}
}

/**
 * Built-in functions available in formulas
 */
const FUNCTIONS = {
	// Conditional
	IF: (args, context) => {
		if (args.length < 3) throw new Error('IF requires 3 arguments: IF(condition, trueValue, falseValue)');
		const condition = evaluate(args[0], context);
		return condition ? evaluate(args[1], context) : evaluate(args[2], context);
	},

	// Math functions
	ABS: (args, context) => Math.abs(evaluate(args[0], context)),
	ROUND: (args, context) => {
		const value = evaluate(args[0], context);
		const decimals = args.length > 1 ? evaluate(args[1], context) : 0;
		const factor = Math.pow(10, decimals);
		return Math.round(value * factor) / factor;
	},
	FLOOR: (args, context) => Math.floor(evaluate(args[0], context)),
	CEIL: (args, context) => Math.ceil(evaluate(args[0], context)),
	MIN: (args, context) => Math.min(...args.map((a) => evaluate(a, context))),
	MAX: (args, context) => Math.max(...args.map((a) => evaluate(a, context))),
	POW: (args, context) => Math.pow(evaluate(args[0], context), evaluate(args[1], context)),

	// String functions
	CONCAT: (args, context) => args.map((a) => String(evaluate(a, context))).join(''),
	UPPER: (args, context) => String(evaluate(args[0], context)).toUpperCase(),
	LOWER: (args, context) => String(evaluate(args[0], context)).toLowerCase(),
	LEFT: (args, context) => {
		const str = String(evaluate(args[0], context));
		const len = evaluate(args[1], context);
		return str.substring(0, len);
	},
	RIGHT: (args, context) => {
		const str = String(evaluate(args[0], context));
		const len = evaluate(args[1], context);
		return str.substring(str.length - len);
	},
	LEN: (args, context) => String(evaluate(args[0], context)).length,
	TRIM: (args, context) => String(evaluate(args[0], context)).trim(),
	CONTAINS: (args, context) => {
		const str = String(evaluate(args[0], context)).toLowerCase();
		const search = String(evaluate(args[1], context)).toLowerCase();
		return str.includes(search);
	},

	// Null/default handling
	COALESCE: (args, context) => {
		for (const arg of args) {
			const val = evaluate(arg, context);
			if (val !== null && val !== undefined && val !== '') {
				return val;
			}
		}
		return null;
	},
	ISNULL: (args, context) => {
		const val = evaluate(args[0], context);
		return val === null || val === undefined;
	},
	ISNUMBER: (args, context) => {
		const val = evaluate(args[0], context);
		return typeof val === 'number' || (typeof val === 'string' && val !== '' && !isNaN(Number(val)));
	},
	IFNULL: (args, context) => {
		const val = evaluate(args[0], context);
		return val === null || val === undefined ? evaluate(args[1], context) : val;
	},

	// Aggregation functions (these work on arrays in context)
	SUM: (args, context) => {
		if (args.length === 1 && args[0].type === 'field') {
			// Sum a field across all rows (for event aggregation)
			const fieldName = args[0].name;
			if (context._aggregateData && Array.isArray(context._aggregateData)) {
				return context._aggregateData.reduce((sum, row) => {
					return sum + (Number(row[fieldName]) || 0);
				}, 0);
			}
		}
		// Sum the provided values
		return args.reduce((sum, arg) => sum + (Number(evaluate(arg, context)) || 0), 0);
	},
	COUNT: (args, context) => {
		if (args.length === 0 && context._aggregateData) {
			return context._aggregateData.length;
		}
		if (args.length === 1 && args[0].type === 'field') {
			const fieldName = args[0].name;
			if (context._aggregateData && Array.isArray(context._aggregateData)) {
				return context._aggregateData.filter((row) => row[fieldName] != null).length;
			}
		}
		return args.length;
	},
	AVG: (args, context) => {
		if (args.length === 1 && args[0].type === 'field') {
			const fieldName = args[0].name;
			if (context._aggregateData && Array.isArray(context._aggregateData)) {
				const values = context._aggregateData.map((row) => Number(row[fieldName]) || 0);
				return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
			}
		}
		const values = args.map((arg) => evaluate(arg, context));
		return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
	},

	// Logical functions
	AND: (args, context) => args.every((arg) => evaluate(arg, context)),
	OR: (args, context) => args.some((arg) => evaluate(arg, context)),
	NOT: (args, context) => !evaluate(args[0], context),

	// Type conversion
	NUMBER: (args, context) => Number(evaluate(args[0], context)) || 0,
	TEXT: (args, context) => String(evaluate(args[0], context)),

	// String functions
	CONCAT: (args, context) => {
		// Concatenate all arguments into a single string
		return args.map((arg) => String(evaluate(arg, context) || '')).join('');
	},
	REPLACE: (args, context) => {
		// Replace all occurrences of find with replacement
		const text = String(evaluate(args[0], context) || '');
		const find = String(evaluate(args[1], context) || '');
		const replacement = args.length > 2 ? String(evaluate(args[2], context) || '') : '';
		if (!find) return text;
		return text.split(find).join(replacement);
	},
	SUBSTITUTE: (args, context) => {
		// Alias for REPLACE (Excel compatibility)
		const text = String(evaluate(args[0], context) || '');
		const find = String(evaluate(args[1], context) || '');
		const replacement = args.length > 2 ? String(evaluate(args[2], context) || '') : '';
		if (!find) return text;
		return text.split(find).join(replacement);
	},
	TRIM: (args, context) => {
		// Remove leading/trailing whitespace
		return String(evaluate(args[0], context) || '').trim();
	},
	UPPER: (args, context) => {
		return String(evaluate(args[0], context) || '').toUpperCase();
	},
	LOWER: (args, context) => {
		return String(evaluate(args[0], context) || '').toLowerCase();
	},
	LEFT: (args, context) => {
		const text = String(evaluate(args[0], context) || '');
		const numChars = args.length > 1 ? Number(evaluate(args[1], context)) || 1 : 1;
		return text.substring(0, numChars);
	},
	RIGHT: (args, context) => {
		const text = String(evaluate(args[0], context) || '');
		const numChars = args.length > 1 ? Number(evaluate(args[1], context)) || 1 : 1;
		return text.substring(text.length - numChars);
	},
	MID: (args, context) => {
		const text = String(evaluate(args[0], context) || '');
		const start = Number(evaluate(args[1], context)) || 0;
		const numChars = args.length > 2 ? Number(evaluate(args[2], context)) : text.length;
		return text.substring(start, start + numChars);
	},

	// Array functions
	INDEX: (args, context) => {
		const arr = evaluate(args[0], context);
		const index = evaluate(args[1], context);
		if (Array.isArray(arr) && index >= 0 && index < arr.length) {
			return arr[index];
		}
		return null;
	},
	SPLIT: (args, context) => {
		const str = String(evaluate(args[0], context) || '');
		const delimiter = args.length > 1 ? String(evaluate(args[1], context)) : ',';
		return str.split(delimiter);
	},
	LENGTH: (args, context) => {
		const val = evaluate(args[0], context);
		if (Array.isArray(val)) return val.length;
		if (typeof val === 'string') return val.length;
		return 0;
	},
	JOIN: (args, context) => {
		const arr = evaluate(args[0], context);
		const delimiter = args.length > 1 ? String(evaluate(args[1], context)) : ',';
		if (Array.isArray(arr)) return arr.join(delimiter);
		return String(arr || '');
	},
	MATCH: (args, context) => {
		const arr = evaluate(args[0], context);
		if (!Array.isArray(arr)) return '';
		// Remaining args are the candidate values to search for
		for (let i = 1; i < args.length; i++) {
			const candidate = String(evaluate(args[i], context));
			if (arr.some((t) => String(t).toLowerCase() === candidate.toLowerCase())) {
				return candidate;
			}
		}
		return '';
	},

	// Date functions — use UTC getters so results are timezone-invariant
	YEAR: (args, context) => {
		const val = evaluate(args[0], context);
		if (typeof val === 'number') {
			return new Date(val * 1000).getUTCFullYear();
		}
		return new Date(val).getUTCFullYear();
	},
	MONTH: (args, context) => {
		const val = evaluate(args[0], context);
		if (typeof val === 'number') {
			return new Date(val * 1000).getUTCMonth() + 1;
		}
		return new Date(val).getUTCMonth() + 1;
	},
};

/**
 * Evaluate an AST node with the given context
 * @param {Object} node - AST node to evaluate
 * @param {Object} context - Context with field values
 * @returns {*} Evaluated result
 */
function evaluate(node, context) {
	if (!node) return null;

	switch (node.type) {
		case 'number':
			return node.value;

		case 'string':
			return node.value;

		case 'field': {
			// Look up field value in context
			const fieldName = node.name;

			// Check resolved values first (columns already in the report)
			if (context.resolvedValues && fieldName in context.resolvedValues) {
				return context.resolvedValues[fieldName];
			}

			// Check direct context (for simple field names)
			if (fieldName in context) {
				return context[fieldName];
			}

			// Handle dot notation paths (e.g., receiver.tags, user.shares.C.owned, calc.totalValue)
			if (fieldName.includes('.')) {
				// Lazily compute calculator results when calc.* or user.* paths are accessed
				if (fieldName.startsWith('calc.') || fieldName.startsWith('user.')) ensureCalcResult(context);
				const parts = fieldName.split('.');
				let value = context;
				for (const part of parts) {
					if (value && typeof value === 'object' && part in value) {
						value = value[part];
					} else {
						return null;
					}
				}
				return value;
			}

			// Try to resolve using FieldRegistry and ColumnResolvers
			const fieldDef = getFieldByKey(fieldName, context.clientData);
			if (fieldDef) {
				return resolveColumnValue(fieldDef, context, {});
			}

			// Legacy: Check underscore paths (e.g., user_name -> user.name)
			if (fieldName.includes('_')) {
				const dotPath = fieldName.replace(/_/g, '.');
				const parts = dotPath.split('.');
				let value = context;
				for (const part of parts) {
					if (value && typeof value === 'object' && part in value) {
						value = value[part];
					} else {
						return null;
					}
				}
				return value;
			}

			return null;
		}

		case 'binary': {
			const left = evaluate(node.left, context);
			const right = evaluate(node.right, context);

			// Coerce to number, stripping formatted strings (e.g., "1,000" -> 1000)
			const toNum = (v) => {
				if (v == null) return 0;
				if (typeof v === 'number') return v;
				if (typeof v === 'string') {
					const cleaned = v.replace(/[^-.\d]/g, '');
					return Number(cleaned) || 0;
				}
				return Number(v) || 0;
			};

			switch (node.operator) {
				case '+':
					return toNum(left) + toNum(right);
				case '-':
					return toNum(left) - toNum(right);
				case '*':
					return toNum(left) * toNum(right);
				case '/':
					const divisor = toNum(right);
					return divisor !== 0 ? toNum(left) / divisor : 0;
				default:
					return 0;
			}
		}

		case 'unary':
			if (node.operator === '-') {
				return -(Number(evaluate(node.operand, context)) || 0);
			}
			return evaluate(node.operand, context);

		case 'comparison': {
			const left = evaluate(node.left, context);
			const right = evaluate(node.right, context);

			switch (node.operator) {
				case '==':
					return left == right;
				case '!=':
					return left != right;
				case '<':
					return left < right;
				case '>':
					return left > right;
				case '<=':
					return left <= right;
				case '>=':
					return left >= right;
				default:
					return false;
			}
		}

		case 'function': {
			const func = FUNCTIONS[node.name];
			if (!func) {
				throw new Error(`Unknown function: ${node.name}`);
			}
			return func(node.args, context);
		}

		default:
			throw new Error(`Unknown node type: ${node.type}`);
	}
}

/**
 * FormulaEngine class - main interface for formula evaluation
 */
export class FormulaEngine {
	/**
	 * Parse a formula string into an AST
	 * @param {string} formula - Formula string
	 * @returns {Object} AST
	 */
	static parse(formula) {
		const tokens = tokenize(formula);
		const parser = new Parser(tokens);
		return parser.parse();
	}

	/**
	 * Evaluate a formula with the given context
	 * @param {string} formula - Formula string
	 * @param {Object} context - Context with field values
	 * @returns {*} Result of evaluation
	 */
	static evaluate(formula, context) {
		try {
			const ast = FormulaEngine.parse(formula);
			return evaluate(ast, context);
		} catch (error) {
			console.error('Formula evaluation error:', error.message, 'Formula:', formula);
			return null;
		}
	}

	/**
	 * Validate a formula without evaluating it
	 * @param {string} formula - Formula string
	 * @returns {Object} { valid: boolean, error?: string }
	 */
	static validate(formula) {
		try {
			FormulaEngine.parse(formula);
			return { valid: true };
		} catch (error) {
			return { valid: false, error: error.message };
		}
	}

	/**
	 * Get list of available functions
	 * @returns {Array} Array of function definitions
	 */
	static getAvailableFunctions() {
		return [
			// Conditional
			{
				name: 'IF',
				syntax: 'IF(condition, trueValue, falseValue)',
				description: 'Returns trueValue if condition is true, otherwise falseValue',
				category: 'Conditional',
			},

			// Math
			{ name: 'ABS', syntax: 'ABS(number)', description: 'Returns absolute value', category: 'Math' },
			{ name: 'ROUND', syntax: 'ROUND(number, decimals)', description: 'Rounds to specified decimal places', category: 'Math' },
			{ name: 'FLOOR', syntax: 'FLOOR(number)', description: 'Rounds down to nearest integer', category: 'Math' },
			{ name: 'CEIL', syntax: 'CEIL(number)', description: 'Rounds up to nearest integer', category: 'Math' },
			{ name: 'MIN', syntax: 'MIN(a, b, ...)', description: 'Returns minimum value', category: 'Math' },
			{ name: 'MAX', syntax: 'MAX(a, b, ...)', description: 'Returns maximum value', category: 'Math' },
			{ name: 'POW', syntax: 'POW(base, exponent)', description: 'Returns base raised to exponent', category: 'Math' },

			// String
			{ name: 'CONCAT', syntax: 'CONCAT(a, b, ...)', description: 'Concatenates strings', category: 'Text' },
			{ name: 'UPPER', syntax: 'UPPER(text)', description: 'Converts to uppercase', category: 'Text' },
			{ name: 'LOWER', syntax: 'LOWER(text)', description: 'Converts to lowercase', category: 'Text' },
			{ name: 'LEFT', syntax: 'LEFT(text, length)', description: 'Returns leftmost characters', category: 'Text' },
			{ name: 'RIGHT', syntax: 'RIGHT(text, length)', description: 'Returns rightmost characters', category: 'Text' },
			{ name: 'LEN', syntax: 'LEN(text)', description: 'Returns length of text', category: 'Text' },
			{ name: 'TRIM', syntax: 'TRIM(text)', description: 'Removes leading/trailing spaces', category: 'Text' },
			{ name: 'CONTAINS', syntax: 'CONTAINS(text, search)', description: 'Returns true if text contains search (case-insensitive)', category: 'Text' },

			// Null handling
			{ name: 'COALESCE', syntax: 'COALESCE(a, b, ...)', description: 'Returns first non-null value', category: 'Null Handling' },
			{ name: 'IFNULL', syntax: 'IFNULL(value, default)', description: 'Returns default if value is null', category: 'Null Handling' },
			{ name: 'ISNULL', syntax: 'ISNULL(value)', description: 'Returns true if value is null', category: 'Null Handling' },
			{ name: 'ISNUMBER', syntax: 'ISNUMBER(value)', description: 'Returns true if value is numeric', category: 'Null Handling' },

			// Aggregation
			{ name: 'SUM', syntax: 'SUM(field) or SUM(a, b, ...)', description: 'Sums values', category: 'Aggregation' },
			{ name: 'COUNT', syntax: 'COUNT(field)', description: 'Counts non-null values', category: 'Aggregation' },
			{ name: 'AVG', syntax: 'AVG(field) or AVG(a, b, ...)', description: 'Calculates average', category: 'Aggregation' },

			// Logical
			{ name: 'AND', syntax: 'AND(a, b, ...)', description: 'Returns true if all arguments are true', category: 'Logical' },
			{ name: 'OR', syntax: 'OR(a, b, ...)', description: 'Returns true if any argument is true', category: 'Logical' },
			{ name: 'NOT', syntax: 'NOT(value)', description: 'Returns opposite boolean', category: 'Logical' },

			// Type conversion
			{ name: 'NUMBER', syntax: 'NUMBER(value)', description: 'Converts to number', category: 'Conversion' },
			{ name: 'TEXT', syntax: 'TEXT(value)', description: 'Converts to text', category: 'Conversion' },

			// Array functions
			{ name: 'INDEX', syntax: 'INDEX(array, position)', description: 'Gets element at position from array (0-based)', category: 'Array' },
			{ name: 'SPLIT', syntax: 'SPLIT(text, delimiter)', description: 'Splits text into array by delimiter', category: 'Array' },
			{ name: 'LENGTH', syntax: 'LENGTH(array)', description: 'Returns length of array or string', category: 'Array' },
			{ name: 'JOIN', syntax: 'JOIN(array, delimiter)', description: 'Joins array elements into string', category: 'Array' },
			{
				name: 'MATCH',
				syntax: 'MATCH(array, value1, value2, ...)',
				description: 'Returns first matching value found in array (case-insensitive)',
				category: 'Array',
			},

			// Date
			{ name: 'YEAR', syntax: 'YEAR(date)', description: 'Returns year from date', category: 'Date' },
			{ name: 'MONTH', syntax: 'MONTH(date)', description: 'Returns month from date (1-12)', category: 'Date' },
		];
	}

	/**
	 * Get list of available operators
	 * @returns {Array} Array of operator definitions
	 */
	static getAvailableOperators() {
		return [
			{ symbol: '+', name: 'Add', description: 'Addition or string concatenation' },
			{ symbol: '-', name: 'Subtract', description: 'Subtraction' },
			{ symbol: '*', name: 'Multiply', description: 'Multiplication' },
			{ symbol: '/', name: 'Divide', description: 'Division (returns 0 if dividing by 0)' },
			{ symbol: '==', name: 'Equals', description: 'Equal comparison' },
			{ symbol: '!=', name: 'Not Equals', description: 'Not equal comparison' },
			{ symbol: '<', name: 'Less Than', description: 'Less than comparison' },
			{ symbol: '>', name: 'Greater Than', description: 'Greater than comparison' },
			{ symbol: '<=', name: 'Less or Equal', description: 'Less than or equal comparison' },
			{ symbol: '>=', name: 'Greater or Equal', description: 'Greater than or equal comparison' },
		];
	}

	/**
	 * Get common formula templates for a given context (reportBuilder or capTable).
	 * @param {string} context - One of 'reportBuilder' or 'capTable'
	 * @returns {Array<{key: string, name: string, description: string, formula: string}>}
	 */
	static getCommonFormulaTemplates(context) {
		const ctx = context === 'capTable' ? 'capTable' : 'reportBuilder';
		const templates = [
			{ key: 'perShare', name: 'Per Share Amount', reportBuilder: 'allocationAmount / sharesOwned', capTable: 'owned / totalShares' },
			{ key: 'percentage', name: 'Percentage of Total', reportBuilder: 'amount / total * 100', capTable: 'owned / totalShares * 100' },
			{
				key: 'safeDiv',
				name: 'Safe Division',
				reportBuilder: 'IF(divisor != 0, value / divisor, 0)',
				capTable: 'IF(totalShares != 0, owned / totalShares, 0)',
			},
			{ key: 'roundCurrency', name: 'Round to Cents', reportBuilder: 'ROUND(amount, 2)', capTable: 'ROUND(owned, 2)' },
		];
		return templates.map((t) => ({
			key: t.key,
			name: t.name,
			description: t[ctx],
			formula: t[ctx],
		}));
	}

	/**
	 * Extract field references from a formula
	 * @param {string} formula - Formula string
	 * @returns {Array} Array of field names referenced
	 */
	static extractFieldReferences(formula) {
		const fields = new Set();

		function extractFromNode(node) {
			if (!node) return;

			if (node.type === 'field') {
				fields.add(node.name);
			} else if (node.type === 'binary' || node.type === 'comparison') {
				extractFromNode(node.left);
				extractFromNode(node.right);
			} else if (node.type === 'unary') {
				extractFromNode(node.operand);
			} else if (node.type === 'function') {
				node.args.forEach(extractFromNode);
			}
		}

		try {
			const ast = FormulaEngine.parse(formula);
			extractFromNode(ast);
		} catch (e) {
			// Ignore parse errors
		}

		return Array.from(fields);
	}
}

